import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Building2, ChevronLeft, ChevronRight, KeyRound, Download, RotateCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/modules/config";
import { useLang } from "@/i18n/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}


export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [fx, setFx] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [range, setRange] = useState(() => ({ from: localToday(), to: localToday() }));
  const [cashflow, setCashflow] = useState(null);
  const [weekStart, setWeekStart] = useState(localToday());
  const [tick, setTick] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (user?.role === "super_admin") return;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [user]);

  useEffect(() => {
    if (user?.role === "super_admin") return;
    (async () => {
      try {
        const params = { date_from: range.from, date_to: range.to };
        const { data } = await api.get("/dashboard/upcoming", { params });
        setUpcoming(data);
        setLastUpdate(new Date().toLocaleTimeString("tr-TR"));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [user, range, tick]);

  const loadCashflow = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/cashflow", { params: { week_start: weekStart } });
      setCashflow(data);
    } catch {
      setCashflow(null);
    }
  }, [weekStart]);

  useEffect(() => {
    if (user?.role === "super_admin") return;
    loadCashflow();
  }, [user, tick, loadCashflow]);

  useEffect(() => {
    if (user?.role === "super_admin") return;
    const handle = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(handle);
  }, [user]);

  useEffect(() => {
    if (user?.role === "super_admin") return;
    api.get("/fx/rates").then(({ data }) => setFx(data)).catch(() => {});
  }, [user]);

  const refreshFx = async () => {
    setFxLoading(true);
    try {
      const { data } = await api.get("/fx/rates", { params: { refresh: 1 } });
      setFx(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFxLoading(false);
    }
  };

  const exportUpcomingPdf = async (kind) => {
    try {
      const params = { kind, date_from: range.from, date_to: range.to };
      const { data } = await api.get("/dashboard/upcoming.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `yaklasan_${kind}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("PDF indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const shiftWeek = (delta) => {
    const d = new Date(`${weekStart}T00:00:00`);
    d.setDate(d.getDate() + delta);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const exportCashflowPdf = async () => {
    try {
      const { data } = await api.get("/dashboard/cashflow.pdf", {
        params: { week_start: weekStart }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "nakit_akis.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("PDF indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user?.role === "super_admin") {
    return (
      <div className="fade-up" data-testid="superadmin-dashboard">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Süper Admin Paneli")}</h1>
        <p className="mt-2 text-slate-500">{t("TEMHA platformundaki tüm firmaları yönetin.")}</p>
        {user?.must_change_password && <PasswordChangeBanner />}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/admin/tenants" data-testid="goto-tenants-card"
            className="border border-slate-200 bg-white rounded-md p-8 hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow] duration-200 block">
            <Building2 className="h-8 w-8 text-blue-600" />
            <h2 className="font-display text-lg font-semibold text-[#0A1128] mt-4">{t("Firmalar")}</h2>
            <p className="text-sm text-slate-500 mt-1">{t("Tenant firmaları görüntüleyin, aktif/pasif durumlarını yönetin.")}</p>
          </Link>
          <Link to="/settings/profile" data-testid="goto-profile-card"
            className="border border-slate-200 bg-white rounded-md p-8 hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow] duration-200 block">
            <KeyRound className="h-8 w-8 text-blue-600" />
            <h2 className="font-display text-lg font-semibold text-[#0A1128] mt-4">{t("Profil Ayarları")}</h2>
            <p className="text-sm text-slate-500 mt-1">{t("E-posta ve şifrenizi güncelleyin.")}</p>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up" data-testid="tenant-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]" data-testid="dashboard-org-name">
            {summary?.organization?.name || t("Panel")}
          </h1>
          <p className="mt-2 text-slate-500">{t("Firmanızın finansal görünümüne hoş geldiniz.")}</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-testid="org-status-badge">
            {summary?.organization?.status === "active" ? t("Aktif") : summary?.organization?.status}
          </Badge>
          <Badge variant="outline" className="border-blue-300 text-blue-700" data-testid="org-plan-badge">
            {summary?.organization?.plan === "trial" ? t("Deneme") : summary?.organization?.plan}
          </Badge>
        </div>
      </div>

      {user?.must_change_password && <PasswordChangeBanner />}

      <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="upcoming-date-filter">
        <span className="text-sm text-slate-500">{t("Tarih aralığı")}:</span>
        <Input type="date" value={range.from}
          onChange={(e) => setRange((r) => ({ ...r, from: e.target.value || r.from }))}
          data-testid="upcoming-date-from" className="w-44 bg-white focus-visible:ring-blue-600" />
        <span className="text-slate-400">–</span>
        <Input type="date" value={range.to}
          onChange={(e) => setRange((r) => ({ ...r, to: e.target.value || r.to }))}
          data-testid="upcoming-date-to" className="w-44 bg-white focus-visible:ring-blue-600" />
        <Button variant="outline" size="sm" onClick={() => setRange({ from: localToday(), to: localToday() })}
          data-testid="upcoming-today-button" className="border-slate-300 text-slate-700 hover:bg-slate-100">
          {t("Bugün")}
        </Button>
        <Button variant="outline" size="sm" data-testid="upcoming-month-button"
          onClick={() => {
            const now = new Date();
            const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            setRange({ from: first, to: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}` });
          }}
          className="border-slate-300 text-slate-700 hover:bg-slate-100">
          {t("Bu Ay")}
        </Button>
        <span className="text-xs text-slate-400">{t("Seçilen tarih aralığındaki ödemeler ve tahsilatlar listelenir.")}</span>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UpcomingBox
          title="Yaklaşan Ödemeler"
          items={upcoming?.payments ?? []}
          accent="red"
          emptyText="Yaklaşan ödeme yok"
          testid="upcoming-payments"
          onExport={() => exportUpcomingPdf("payments")}
        />
        <UpcomingBox
          title="Yaklaşan Tahsilatlar"
          items={upcoming?.collections ?? []}
          accent="green"
          emptyText="Yaklaşan tahsilat yok"
          testid="upcoming-collections"
          onExport={() => exportUpcomingPdf("collections")}
        />
      </div>

      {cashflow && (
        <div className="mt-8 border border-slate-200 bg-white rounded-md p-6 fade-up" data-testid="cashflow-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0A1128] flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                {t("Haftalık Nakit Akışı")}
              </h2>
              <p className="mt-0.5 text-xs text-slate-400" data-testid="cashflow-range">
                {formatDate(cashflow.week_start)} — {formatDate(cashflow.days[6]?.date)} · {t("7 günlük ödeme/tahsilat dağılımı")}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="icon" onClick={() => shiftWeek(-7)} aria-label={t("Önceki hafta")}
                data-testid="cashflow-prev" className="h-8 w-8 border-slate-300 text-slate-600 hover:bg-slate-100">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input type="date" value={weekStart} data-testid="cashflow-date"
                onChange={(e) => e.target.value && setWeekStart(e.target.value)}
                className="w-40 h-8 bg-white focus-visible:ring-blue-600" />
              <Button variant="outline" size="icon" onClick={() => shiftWeek(7)} aria-label={t("Sonraki hafta")}
                data-testid="cashflow-next" className="h-8 w-8 border-slate-300 text-slate-600 hover:bg-slate-100">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setWeekStart(localToday())}
                data-testid="cashflow-this-week" className="h-8 border-slate-300 text-slate-700 hover:bg-slate-100">
                {t("Bu Hafta")}
              </Button>
              <Button variant="outline" size="sm" onClick={exportCashflowPdf}
                data-testid="cashflow-pdf-button" className="h-8 border-slate-300 text-slate-700 hover:bg-slate-100">
                <Download className="h-4 w-4 mr-1.5" />
                {t("PDF Rapor")}
              </Button>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-4">
            <div className="border border-slate-100 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("Toplam Ödeme")}</p>
              <p className="mt-1 text-lg font-semibold tabular text-red-600" data-testid="cashflow-total-payments">
                {formatMoney(cashflow.total_payments)}
              </p>
            </div>
            <div className="border border-slate-100 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("Toplam Tahsilat")}</p>
              <p className="mt-1 text-lg font-semibold tabular text-green-600" data-testid="cashflow-total-collections">
                {formatMoney(cashflow.total_collections)}
              </p>
            </div>
            <div className="border border-slate-100 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("Net Akış")}</p>
              <p className={`mt-1 text-lg font-semibold tabular ${cashflow.net >= 0 ? "text-green-700" : "text-red-600"}`}
                data-testid="cashflow-net">
                {formatMoney(cashflow.net)}
              </p>
            </div>
          </div>
          <CashflowChart days={cashflow.days} />
        </div>
      )}

      {fx?.rates && Object.keys(fx.rates).length > 0 && (
        <div className="mt-8 w-full max-w-sm border border-slate-200 bg-white rounded-md p-6 fade-up" data-testid="fx-rates">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A1128]">{t("TCMB Efektif Satış")}</h3>
              <p className="mt-0.5 text-xs text-slate-400" data-testid="fx-date">{fx.date ? formatDate(fx.date) : "—"}</p>
            </div>
            <button type="button" onClick={refreshFx} disabled={fxLoading} title={t("Yenile")}
              data-testid="fx-refresh-button"
              className="rounded-md p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-150 disabled:opacity-50">
              <RotateCw className={`h-4 w-4 ${fxLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {["USD", "EUR", "GBP", "CHF"].map((c) => fx.rates[c] && (
              <div key={c} className="flex items-center justify-between border border-slate-100 rounded-md px-3 py-2.5 hover:bg-slate-50 transition-colors duration-200" data-testid={`fx-rate-${c}`}>
                <span className="w-12 text-sm font-semibold text-[#0A1128]">{c}</span>
                <div className="flex gap-8 text-sm tabular">
                  <span className="text-right">
                    <span className="block text-[10px] uppercase tracking-wider text-slate-400">{t("Alış")}</span>
                    <span className="text-[#0A1128]">{fx.rates[c].buy.toLocaleString("tr-TR", { minimumFractionDigits: 4 })}</span>
                  </span>
                  <span className="text-right">
                    <span className="block text-[10px] uppercase tracking-wider text-slate-400">{t("Satış")}</span>
                    <span className="font-semibold text-green-700" data-testid={`fx-sell-${c}`}>
                      {fx.rates[c].sell.toLocaleString("tr-TR", { minimumFractionDigits: 4 })}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 border-t border-slate-100 pt-3 text-[10px] leading-relaxed text-slate-400">
            {t("Kaynak: Türkiye Cumhuriyet Merkez Bankası · Her iş günü 15:30'da güncellenir")}
          </p>
        </div>
      )}

      {lastUpdate && (
        <p className="mt-8 text-center text-[11px] text-slate-400" data-testid="dash-auto-refresh-note">
          {t("Son güncelleme")}: {lastUpdate} · {t("Otomatik yenileme: 60sn")}
        </p>
      )}
    </div>
  );
}

const WEEKDAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function CashflowChart({ days }) {
  const { t } = useLang();
  const maxVal = Math.max(1, ...days.map((d) => Math.max(d.payments, d.collections)));
  return (
    <div className="mt-6">
      <div className="flex items-end gap-3 h-40" data-testid="cashflow-chart">
        {days.map((d) => {
          const wd = WEEKDAYS_TR[(new Date(`${d.date}T00:00:00`).getDay() + 6) % 7];
          const ph = d.payments > 0 ? Math.max((d.payments / maxVal) * 130, 4) : 0;
          const ch = d.collections > 0 ? Math.max((d.collections / maxVal) * 130, 4) : 0;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`cashflow-day-${d.date}`}>
              <div className="flex items-end gap-1 h-[130px]">
                <div className="w-3.5 rounded-t bg-green-500/90 transition-[height] duration-300"
                  style={{ height: ch }} title={`${t("Tahsilatlar")}: ${formatMoney(d.collections)}`} />
                <div className="w-3.5 rounded-t bg-red-500/90 transition-[height] duration-300"
                  style={{ height: ph }} title={`${t("Ödemeler")}: ${formatMoney(d.payments)}`} />
              </div>
              <span className="text-[10px] text-slate-400 tabular whitespace-nowrap">
                {wd} {d.date.slice(8, 10)}.{d.date.slice(5, 7)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-center gap-5 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-green-500/90" />{t("Tahsilatlar")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/90" />{t("Ödemeler")}</span>
      </div>
    </div>
  );
}

function UpcomingBox({ title, items, accent, emptyText, testid, onExport }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t(title)}</h2>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-1 rounded ${
            accent === "red" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
          }`}>
            {items.length}
          </span>
          {onExport && (
            <Button variant="ghost" size="icon" onClick={onExport} aria-label={t("PDF olarak indir")}
              data-testid={`${testid}-pdf-button`}
              className="h-7 w-7 text-slate-500 hover:text-blue-600 hover:bg-blue-50">
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">{t(emptyText)}</p>
        ) : (
          items.map((it, i) => {
            const overdue = it.due_date && it.due_date < today;
            return (
              <div key={i} className="flex items-center gap-3 border border-slate-100 rounded-md px-3 py-2.5 hover:bg-slate-50 transition-colors duration-200">
                <div className={`text-xs tabular font-medium w-20 shrink-0 ${overdue ? "text-red-600" : "text-slate-500"}`}>
                  {formatDate(it.due_date)}
                  {overdue && <span className="block text-[10px] text-red-500">{t("Gecikti")}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0A1128] truncate">{it.title}</p>
                  <p className="text-[11px] text-slate-400">{t(it.source)}</p>
                </div>
                <span className="text-sm font-semibold tabular text-[#0A1128]">{formatMoney(it.amount)}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function PasswordChangeBanner() {
  const { t } = useLang();
  return (
    <div className="mt-8 rounded-md border border-amber-200 bg-amber-50 p-4 flex items-center justify-between gap-4 flex-wrap"
      data-testid="password-change-banner">
      <p className="text-sm text-amber-800">
        {t("Güvenliğiniz için ilk girişte şifrenizi değiştirmeniz önerilir.")}
      </p>
      <Button asChild size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100">
        <Link to="/settings/profile" data-testid="goto-change-password-button">{t("Şifremi Değiştir")}</Link>
      </Button>
    </div>
  );
}
